import React, { FC, SyntheticEvent, useEffect, useRef, useState } from 'react'
import { useSetBodyScroll } from '../../hooks/useSetBodyScroll'
import { useFocusTrap } from '../../hooks/useFocusTrap'
import { SidePanelFooter } from './SidePanelFooter'
import { SidePanelHeader } from './SidePanelHeader'
import './style.modules.scss'
import { Button } from '../Button'

type SidePanelPage = {
  sections: React.ReactNode[]
}

export type SidePanelProps = React.HTMLAttributes<HTMLDivElement> & {
  className?: string
  footer?: React.ReactNode
  heading?: React.ReactNode
  minimizable?: boolean
  /** Renders pages in SidePanel. (Note: pages prop is ignored when children are given)  */
  pages?: SidePanelPage[]
  /** Custom trigger Component to open SidePanel. Required for uncontrolled usage. */
  triggerNode?: React.ReactElement
  /** Custom function to update state on close */
  handleCustomClose?: () => void
  /** SidePanel openingDirection to open from. Defaults to right */
  openingDirection?: 'left' | 'right'
  /** Flag to close the side panel (uncontrolled mode) */
  isPanelClose?: boolean
  /** Controlled mode: externally manage panel open state. When provided, triggerNode is not rendered. */
  isOpen?: boolean
  /**
   * When true, applies the `SidePanel--sticky-footer` modifier so the footer
   * stays fixed and content scrolls behind it. A panel can have a sticky header
   * (default) or a sticky footer, but not both. Requires a `footer`.
   */
  stickyFooter?: boolean
  testId?: string
}

export const SidePanelContainer: FC<SidePanelProps> = props => {
  const {
    triggerNode,
    heading,
    minimizable,
    className,
    pages = [],
    footer,
    handleCustomClose,
    children,
    openingDirection = 'right',
    isPanelClose = false,
    isOpen,
    stickyFooter = false,
    testId
  } = props

  const isControlled = isOpen !== undefined

  const [internalIsVisible, setInternalIsVisible] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const setBodyScrollLock = useSetBodyScroll(false)
  const sidePanelRef = useRef<HTMLElement>(null)
  const isMultiPage = !children && pages.length > 1

  const isVisible = isControlled ? isOpen : internalIsVisible

  useFocusTrap(!!isVisible, sidePanelRef, true)

  const prevBtnClassNames =
    'Button Button--icon Button--icon--neutral SidePanel-Button--prev'

  const nextBtnClassNames =
    'Button Button--primary u-marginLeft20 u-marginTop24 SidePanel-Button--next' +
    (currentPage === pages.length - 1 ? ' u-hidden' : '')

  const handleClose = (event?: SyntheticEvent): void => {
    setBodyScrollLock(false)
    if (!isControlled) {
      setInternalIsVisible(false)
    }
    if (handleCustomClose) {
      handleCustomClose()
    }
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  useEffect(() => {
    if (!isControlled && isPanelClose && internalIsVisible) {
      handleClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPanelClose, isControlled, internalIsVisible])

  useEffect(() => {
    setBodyScrollLock(!!isVisible)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible])

  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isVisible) {
        if (!isControlled) {
          setInternalIsVisible(false)
        }
        setBodyScrollLock(false)
        if (handleCustomClose) {
          handleCustomClose()
        }
      }
    }
    document.addEventListener('keydown', handleEscapeKey, false)

    return (): void => {
      document.removeEventListener('keydown', handleEscapeKey, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleCustomClose, isVisible, isControlled, setBodyScrollLock])

  const handleTrigger = (event: SyntheticEvent): void => {
    setInternalIsVisible(true)
    setBodyScrollLock(true)
    event.stopPropagation()
  }

  const toggleMinimized = (event: SyntheticEvent): void => {
    setIsMinimized(prevState => {
      const newState = !prevState
      setBodyScrollLock(!newState)
      return newState
    })
    event.stopPropagation()
  }

  const handleNextPageBtn = (event: SyntheticEvent): void => {
    event.stopPropagation()
    setCurrentPage(currentPage + 1)
  }

  const handlePrevPageBtn = (event: SyntheticEvent): void => {
    event.stopPropagation()
    setCurrentPage(currentPage - 1)
  }

  const renderSections = (sections: React.ReactNode[] = []): JSX.Element[] => {
    return sections.map((section, index) => {
      const classNames =
        'SidePanel-section' +
        (sections.length > 1 && index < sections.length - 1
          ? ' SidePanel-section--withBorder'
          : '')
      return (
        <section className={classNames} key={index}>
          {section}
        </section>
      )
    })
  }

  return (
    <>
      {!isControlled && triggerNode && !isVisible && (
        <Button
          {...triggerNode.props}
          onClick={event => {
            handleTrigger(event)
            triggerNode.props.onClick?.(event)
          }}
          testId={testId ? `${testId}-trigger-button` : undefined}
        >
          {triggerNode.props.children}
        </Button>
      )}

      <aside
        ref={sidePanelRef}
        tabIndex={-1}
        className={[
          'SidePanel',
          className || '',
          isVisible ? 'is-visible' : '',
          isMinimized ? 'is-minimized' : '',
          openingDirection === 'left' ? 'SidePanel--left' : '',
          stickyFooter && footer ? 'SidePanel--sticky-footer' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid={testId ? testId : undefined}
      >
        <section className="SidePanel-main">
          <div className="SidePanel-topWrapper">
            <SidePanelHeader
              prevBtnClassNames={prevBtnClassNames}
              handlePrevPageBtn={handlePrevPageBtn}
              heading={heading}
              minimizable={minimizable}
              isMinimized={isMinimized}
              toggleMinimized={toggleMinimized}
              handleClose={handleClose}
              showPrevButton={isMultiPage}
              isOnPrevPage={isMultiPage && currentPage > 0}
              testId={testId ? `${testId}-header` : undefined}
            />
            <div className="SidePanel-sectionsWrapper">
              {children || renderSections(pages[currentPage]?.sections)}
            </div>
            {isMultiPage && (
              <button
                className={nextBtnClassNames}
                title="Next page in panel"
                onClick={handleNextPageBtn}
              >
                Next Page
              </button>
            )}
          </div>
          <SidePanelFooter footer={footer} />
        </section>
      </aside>
    </>
  )
}
